import SvgIcon from '@home/components/SvgIcon';
import React, { useMemo } from 'react';
import { Card, ConfigProvider, Justify, Segment, Table } from 'tea-component';
import { useIntl, useSelector } from 'umi';
import { getImg } from '@home/_config/getImgs';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { DEFAULT_PAGING } from '../constants';
import noDataIcon from '@/pages/benchmark/assets/img/no-data.png';
import { IBenchmarkTable, IBenchmarkTableCol } from './type';
import { LANGUAGE_CN, LANGUAGE_EN } from '@home/_config/config';
import classnames from 'classnames';
import { getLanguage } from '@home/utils/common';
import SingleSelect from '@home/components/SingleSelect';
import { getExplainVal } from '@/pages/dashboard/utils/getExplainVal';
import FilterSelect from '@/pages/dashboard/components/FilterSelect';
import './index.scss';
import ACard from '@/pages/dashboard/components/ACard';
import CommonBubble from '@home/components/CommonBubble';

/**
 * 获取指标值，支持 IAA 类型从 text_value_json 中取值
 * @param cvm 行数据
 * @param dataKey 原始数据 key (如 'top10per', 'median')
 * @param textKey text_value_json 中的 key (如 'top10per_text', 'median_text')
 * @returns 对应的值
 */
const getMetricValue = (cvm: IBenchmarkTableCol, dataKey: string, textKey: string) => {
  // 如果是 IAA 类型，优先从 text_value_json 中取值
  if (cvm?.category === 'IAA') {
    const textValue = cvm?.text_value_json?.[textKey];
    if (textValue !== undefined && textValue !== null && textValue !== '') {
      return textValue;
    }
  }
  // 否则返回原始值
  return cvm?.[dataKey as keyof IBenchmarkTableCol];
};

/**
 * 判断是否需要跳过格式化（IAA 类型且使用 text_value_json 的值）
 * @param cvm 行数据
 * @param textKey text_value_json 中的 key
 * @returns 是否跳过格式化
 */
const shouldSkipFormat = (cvm: IBenchmarkTableCol, textKey: string) => {
  if (cvm?.category === 'IAA') {
    const textValue = cvm?.text_value_json?.[textKey];
    return textValue !== undefined && textValue !== null && textValue !== '';
  }
  return false;
};

const BenchmarkMetricsTable = React.memo((props: IBenchmarkTable) => {
  const {
    dataSource,
    onRowClick,
    genreValue,
    genresList,
    onGenreChange,
    categoryValue,
    categoriesList,
    onCategoryChange,
    sourceValue,
    sourcesList,
    onSourceChange,
    paging,
    onPagingChange,
    loading,
    error,
    style,
    className,
    frameType,
    onTabReset
  } = props || {};

  const { formatMessage, locale } = useIntl(); // 中英切换方法
  const isEN = locale === LANGUAGE_EN;
  const { pageable, injectable, autotip } = Table.addons;
  const { list: records = [], row_count: recordCount = 0 } = dataSource || {};
  const { allMetricConfig, explainInfo } = useSelector((state: any) => ({
    allMetricConfig: state?.benchmark?.metricConfig,
    explainInfo: state?.benchmark?.allExplainInfo?.[frameType]
  }));
  // 指标跳转链接生成
  const generateLink = (cvm: IBenchmarkTableCol, genreValue: string | undefined, frameType: string) => {
    const genreQuery = genreValue !== 'ALL' ? `?genre_subgenre=${genreValue}` : '';
    let linkPage;
    if (cvm?.category === 'IAA') {
      linkPage = 'metricIaa';
    } else {
      switch (cvm?.value_type.trim()) {
        case 'otdr':
        case 'otdr,tips':
          linkPage = 'metricOtdr';
          break;
        case 'tips':
          linkPage = 'metricTips';
          break;
        default:
          linkPage = 'metricDetail';
      }
    }
    return `/benchmark/${frameType}/explore/${linkPage}/${cvm?.metric}${genreQuery}`;
  };

  const columns = useMemo(() => {
    const top10Explain = getExplainVal(explainInfo, 'Benchmark Top 10', locale) || '';
    const medianExplain = getExplainVal(explainInfo, 'Benchmark Median', locale);
    const otdrExplain = getExplainVal(explainInfo, 'Benchmark GTDR', locale);

    const columns = [
      {
        key: "metric",
        header: <span className={'tea-text-overflow metrics-header'}>{formatMessage({ id: 'dbb_explore_metric' })}</span>,
        // width: '50%',
        align: "left",
        render: (cvm: IBenchmarkTableCol) => {
          const text = isEN ? cvm?.metric_en : cvm?.metric_cn;
          const metricConfig = allMetricConfig?.[cvm?.metric] || {};
          const { tooltipCn, tooltipEn } = metricConfig || {};
          const explain = getLanguage(isEN, tooltipEn, tooltipCn);
          const link = generateLink(cvm, genreValue, frameType);
          return (
            <ACard key={cvm?.metric} url={link} isStopPropagation={false} style={{ textDecoration: 'none', display: 'block', padding: "10px 10px 10px 0" }}>
              <div className="metrics-table-key-metric">
                {cvm?.icon ? <img src={cvm?.icon} className="type-icon" /> : <SvgIcon type={'Benchmark'} className="type-icon" style={{ color: '#326ff0' }} />}
                <div className="metric-details">
                  <span className="label">
                    <CommonBubble
                      dark trigger={'hover'} placement='top'
                      overlayClassName='explore-bubble'
                      content={explain ? <div dangerouslySetInnerHTML={{ __html: explain.replaceAll('\\n', '<br/>') }}></div> : ''}
                    >
                      <span className="metric-text" title={text}>
                        {text}
                      </span>
                    </CommonBubble>
                    {
                      cvm?.is_new ? (
                        <SvgIcon type="new2" className="metrics-table-metric-new-icon" />
                      ) : <>
                        {
                          cvm?.is_top && (
                            <SvgIcon type="remen" className="flame-icon" />
                          )
                        }
                      </>
                    }
                  </span>
                  <div className="type">
                    <p>{isEN ? cvm?.category_en : cvm?.category_cn || ''}</p>
                    <div className="views">
                      <SvgIcon type="open-eyes" className="eye-icon" />
                      {dataFormat({ value: cvm?.click_num, formatRule: parseFloat(cvm?.click_num) > 1000 ? 'bmk0' : 'int' })}
                    </div>
                  </div>
                </div>
              </div>
            </ACard>
          );
        },
      },
      {
        key: "top",
        align: "center",
        header: (
          <CommonBubble
            overlayClassName='explore-bubble'
            dark trigger={'hover'} placement='top'
            content={top10Explain ? <div dangerouslySetInnerHTML={{ __html: top10Explain.replaceAll('\\n', '<br/>') }}></div> : ''}>
            <span className='tea-text-overflow'>{formatMessage({ id: 'dbb_explore_metric_top_10_percent' })}</span>
          </CommonBubble>
        ),
        width: '12.5%',
        render: (cvm: IBenchmarkTableCol) => {
          const metricConfig = allMetricConfig?.[cvm?.metric] || {};
          const { formatter } = metricConfig || {};
          // 格式化规则
          const metricFormatRule = cvm?.scalar_type === 'percentage' ? 'percent2Decimal' : 'thousandsInit';
          const formatRule = formatter || metricFormatRule;
          const link = generateLink(cvm, genreValue, frameType);
          // 获取值，IAA 类型优先从 text_value_json 取值
          const value = getMetricValue(cvm, 'top10per', 'top10per_text');
          // 判断是否跳过格式化
          const skipFormat = shouldSkipFormat(cvm, 'top10per_text');
          return (
            <ACard key={cvm?.metric} url={link} isStopPropagation={false} style={{ textDecoration: 'none', display: 'block', padding: '19px' }}>
              <span>{skipFormat ? (value ?? '-') : dataFormat({ value, formatRule })}</span>
            </ACard>
          );
        }
      },
      {
        key: "median",
        width: '12.5%',
        align: "center",
        header: (
          <CommonBubble
            overlayClassName='explore-bubble'
            dark trigger={'hover'} placement='top'
            content={medianExplain ? <div dangerouslySetInnerHTML={{ __html: medianExplain.replaceAll('\\n', '<br/>') }}></div> : ''}>
            <span className='tea-text-overflow'>{formatMessage({ id: 'dbb_explore_metric_median' })}</span>
          </CommonBubble>
        ),
        render: (cvm: IBenchmarkTableCol) => {
          const metricConfig = allMetricConfig?.[cvm?.metric] || {};
          const { formatter } = metricConfig || {};
          // 格式化规则
          const metricFormatRule = cvm?.scalar_type === 'percentage' ? 'percent2Decimal' : 'thousandsInit';
          const formatRule = formatter || metricFormatRule;
          const link = generateLink(cvm, genreValue, frameType);
          // 获取值，IAA 类型优先从 text_value_json 取值
          const value = getMetricValue(cvm, 'median', 'median_text');
          // 判断是否跳过格式化
          const skipFormat = shouldSkipFormat(cvm, 'median_text');
          return (
            <ACard key={cvm?.metric} url={link} isStopPropagation={false} style={{ textDecoration: 'none', display: 'block', padding: '19px' }}>
              <span>{skipFormat ? (value ?? '-') : dataFormat({ value, formatRule })}</span>
            </ACard>
          );
        }
      },
      {
        key: "otdr",
        width: '12.5%',
        align: "center",
        header: (
          <CommonBubble
            overlayClassName='explore-bubble'
            dark trigger={'hover'} placement='top'
            content={otdrExplain ? <div dangerouslySetInnerHTML={{ __html: otdrExplain.replaceAll('\\n', '<br/>') }}></div> : ''}>
            <span className='tea-text-overflow'>{formatMessage({ id: 'dbb_explore_metric_otdr' })}</span>
          </CommonBubble>
        ),
        render: (cvm: IBenchmarkTableCol) => {
          const metricConfig = allMetricConfig?.[cvm?.metric] || {};
          const { formatter } = metricConfig || {};
          // 格式化规则
          const metricFormatRule = cvm?.scalar_type === 'percentage' ? 'percent2Decimal' : 'thousandsInit';
          const formatRule = formatter || metricFormatRule;
          const link = generateLink(cvm, genreValue, frameType);
          return (
            <ACard key={cvm?.metric} url={link} isStopPropagation={false} style={{ textDecoration: 'none', display: 'block', padding: '19px' }}>
              <span>{dataFormat({ value: cvm?.otdr, formatRule })}</span>
            </ACard>
          );
        }
      },
      {
        key: 'details',
        header: '',
        width: '10%',
        align: "center",
        render: (cvm: IBenchmarkTableCol) => {
          const link = generateLink(cvm, genreValue, frameType);
          return (
            <ACard key={cvm?.metric} url={link} isStopPropagation={false} style={{ textDecoration: 'none', display: 'block', padding: '19px 0 19px 0px' }}>
              <span className="metrics-table-key-details">{formatMessage({ id: 'dbb_explore_table_detail' })}</span>
            </ACard>
          );
        }
      }
    ];
    const hasOtdr = categoryValue === 'Technical';
    return hasOtdr ? columns : columns.filter((item) => item.key !== 'otdr');

  }, [allMetricConfig, isEN, categoryValue, genreValue, frameType]);

  const addons = useMemo(() => {
    const showPagination = recordCount >= 10 && paging?.pageSize !== undefined && paging.pageSize >= 10;

    const addonsList = [
      autotip({
        isLoading: loading,
        isError: !!error,
        loadingText: formatMessage({ id: "common_loading" }), // 加载文案
        errorText: formatMessage({ id: "common_loading_failed" }),
        style: { lineHeight: '480px' },
        emptyText: (
          <div className="dbb-metrics-table-no-data">
            <img src={noDataIcon} alt="NoDataIcon" className="game-icon" />
            <div className="no-data-title">
              {formatMessage({ id: 'dbb_explore_search_no_found' })}
            </div>
            <p className="no-data-explain">{formatMessage({ id: 'dbb_explore_search_no_found_explain' })}</p>
          </div>
        )
      }),
      injectable({
        row: (props, context) => ({
          onClick: () => {
            onRowClick?.(context?.record);
          }
        })
      })
    ];

    if (showPagination) {
      addonsList.push(
        pageable({
          ...DEFAULT_PAGING,
          stateTextVisible: showPagination,
          pageSizeVisible: showPagination,
          pageIndexVisible: showPagination,
          jumpVisible: showPagination,
          endJumpVisible: showPagination,
          onPagingChange,
          stateText: `${recordCount} ${formatMessage({ id: 'dbb_explore_records' })}`,
          pageIndex: paging?.pageIndex,
          pageSize: paging?.pageSize,
          recordCount
        })
      );
    }

    return addonsList;
  }, [records, loading, error, paging, isEN]);

  const genres = useMemo(() => [
    { text: getLanguage(isEN, 'All', '全部'), value: 'ALL' }, // 添加 'ALL' 选项
    ...(genresList?.map((val: { name_en: string; name_ch: string; code: string; }) => ({
      text: getLanguage(isEN, val?.name_en, val?.name_ch),
      value: val?.code
    })) || [])
  ], [isEN, genresList]);

  const categories = useMemo(() => categoriesList?.map((val: { name_en: string; name_ch: string; code: string; }) => ({
    text: getLanguage(isEN, val?.name_en, val?.name_ch),
    value: val?.code
  })), [isEN, categoriesList]) || [];

  const sources = useMemo(() => [
    ...(sourcesList?.map((val: { name_en: string; name_ch: string; code: string; icon: string; }) => ({
      text: getLanguage(isEN, val?.name_en, val?.name_ch),
      value: val?.code,
      icon: val?.icon || getImg(val?.code),
      render: (item: { text: string; }) => (
        <CommonBubble style={{ maxWidth: '370px' }} content={getExplainVal(explainInfo, item.text, locale)}>
          <span dangerouslySetInnerHTML={{__html: `${item?.text}`}}></span>
        </CommonBubble>
      ),
    })) || [])
  ], [explainInfo, isEN, locale, sourcesList]);

  return (
    <div className={classnames('dbb-metrics-table', className)} style={style}>
      <Table.ActionPanel>
        <Justify
          left={
            <>
              {/* 行容器 */}
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center' }}>
                {/* 指标分类Tabs */}
                <Segment
                  className="dbb-metrics-table-segment"
                  value={categoryValue}
                  options={[
                    { text: formatMessage({ id: 'dbb_explore_metric_all' }), value: 'ALL' },
                    ...categories
                  ]}
                  onChange={onCategoryChange}
                />
                {/* 品类筛选器 和 数据源筛选器 */}
                <div className="dbb-metrics-table-filter">
                  <SingleSelect
                    locale={isEN ? LANGUAGE_EN : LANGUAGE_CN}
                    className='dbb-explore-genre-button'
                    boxClassName="dbb-explore-genre-dropdown"
                    options={genres}
                    value={genreValue}
                    onChange={onGenreChange}
                    name={getLanguage(isEN, 'Genre', '品类')}
                    listWidth={152} tooltip={""}
                  />
                  <FilterSelect
                    value={sourceValue}
                    locale={isEN ? LANGUAGE_EN : LANGUAGE_CN}
                    className='dbb-select-data-source'
                    defaultValue={['ALL']}
                    name={getLanguage(isEN, 'Data Source', '数据源')}
                    placeholder='All'
                    tooltip=''
                    listWidth={300}
                    clickClose={false}
                    closeOnScroll={false}
                    onConfirm={onSourceChange}
                    isRevertToLastSelection={true}
                    radioOptions={[
                      { value: 'ALL', text: getLanguage(isEN, 'All', '全部'), info: '' }
                    ]}
                    options={sources}
                  />
                  <div className='dbb-explore-metrics-table-reset' onClick={onTabReset}>
                    {formatMessage({ id: 'dbb_explore_metrics_table_reset' })}
                  </div>
                </div>

              </div>
            </>
          }
        />
      </Table.ActionPanel>

      <Card>
        <ConfigProvider locale={isEN ? 'en' : 'zh'}>
          <Table
            className="metrics-table"
            verticalTop
            records={records}
            columns={columns}
            addons={addons}
          />
        </ConfigProvider>
      </Card>
    </div>
  );
});

BenchmarkMetricsTable.displayName = 'BenchmarkMetricsTable';

export default BenchmarkMetricsTable;